// hooks/useViewingStatus.jsx


import { getDatabase, ref, get } from 'firebase/database';
import { useAuth } from './useAuth';

const useViewingStatus = () => {
  const { currentUser } = useAuth();
  const database = getDatabase();

  // Helper function to format date/time for each viewing (specific dates)
  const formatDateTime = (displayText, startTime, endTime) => {
    let formattedDate = 'Unknown Date';
    let formattedTime = 'Unknown Time';

    if (displayText) {
      // Extract from "Specific Date: June 23, 2025, from 10:15 AM to 10:30 AM"
      const match = displayText.match(
        /Specific Date:\s*([A-Za-z]+\s+\d{1,2},\s*\d{4}),\s*from\s*([\d:]+\s*[APMapm]{2})\s*to\s*([\d:]+\s*[APMapm]{2})/
      );
      if (match) {
        const [, dateStr, start, end] = match;
        const dateObj = new Date(dateStr);
        formattedDate = !isNaN(dateObj.getTime())
          ? dateObj.toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : dateStr;
        formattedTime = `${start} - ${end}`;
      } else {
        // fallback: just show displayText
        formattedDate = displayText;
        formattedTime = '';
      }
    } else if (startTime && endTime) {
      formattedTime = `${new Date(startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return { formattedDate, formattedTime };
  };

  // Fetch user's scheduled viewings
  const checkViewingStatus = async () => {
    if (!currentUser) throw new Error('User is not authenticated.');

    try {
      const listingsRef = ref(database, 'listings');
      const listingsSnapshot = await get(listingsRef);

      if (!listingsSnapshot.exists()) return [];

      const listings = listingsSnapshot.val();
      const userViewings = [];

      for (const listingId of Object.keys(listings)) {
        const listingData = listings[listingId];
        const viewingRequests = listingData?.viewingRequests || {};

        Object.entries(viewingRequests).forEach(([requestId, request]) => {
          if (request.uid === currentUser.uid) {
            const { dateDetails } = request;
            const { formattedDate, formattedTime } = dateDetails
              ? formatDateTime(dateDetails.displayText, dateDetails.startTime, dateDetails.endTime)
              : { formattedDate: 'Unknown Date', formattedTime: 'Unknown Time' };

            let status =
              typeof request.status !== "string" ||
              !request.status.trim()
                ? "pending"
                : request.status.toLowerCase();

            userViewings.push({
              id: requestId,
              listingId: listingId,
              listingTitle: listingData?.title || 'Unknown Listing',
              address: listingData?.address || 'No address provided',
              city: listingData?.city || 'Unknown City',
              state: listingData?.state || 'Unknown State',
              date: formattedDate,
              time: formattedTime,
              status,
              submittedAt: request.submittedAt || null,
              // --- Pass through all raw fields needed for UI ---
              dateDetails: request.dateDetails || {},
              zoom: request.zoom || {},
              questionnaire: request.questionnaire || {},
              phone: request.phone || "",
              email: request.email || "",
              // add any other raw fields you might need in the UI here!
            });
          }
        });
      }

      return userViewings;
    } catch (error) {
      console.error('Error fetching viewing status:', error);
      throw error;
    }
  };

  return { checkViewingStatus };
};

export default useViewingStatus;
